// alternative-dashboard/CompactWidgets.tsx
import { useEffect, useMemo, useState } from "react";
import type { DashboardLayoutMenuDetail } from "@/app/types/alterNativeDashboardLayout";
import type { BeneficiaryStatusResponse } from "@/app/types/beneficiaries";
import { gxAppStatusApi } from "@/app/apiConfig/services/paymentsAbstractApiLayer";
import { gxIsArray } from "@/app/utils/helpers";
import {
  COMPACT_WIDGET_REGISTRY,
  COMPACT_SUMMARY_MENU_IDS,
} from "./dashboardWidgetRegistryConfig/compactWidgetRegistry.config";
import {
  COMPACT_WIDGET_ROW,
  COMPACT_WIDGET_HIDDEN_MENU_IDS,
} from "./dashboardWidgetRegistryConfig/compactWidgetLayout.config";
import CompactWidgetsCardDetails from "./alternative-components/compactWidgetsCardDetails/CompactWidgetsCardDetails";
import "./alternative-components/styles/AlternativStyle.scss";
import "./alternative-components/compactWidgetsCardDetails/styles/CompactWidgets.scss";
import { useDashboardLayoutData } from "./types/useDashboardLayoutData";
import { useSelector } from "react-redux";
import type { RootState } from "@/app/redux/store";

const OPEN_SUCCESS_MODAL = false;
const CALL_COUNT = false;

const MOCK_REPAIR_QUEUE_DATA = {
  count: 5,
  totalAmount: 1250444.12,
  currencyCode: "AED",
};

function CompactWidgets() {
  const { menuDetail, loading } = useDashboardLayoutData();

  const [statusList, setStatusList] = useState<BeneficiaryStatusResponse[]>([]);
  const callRecent = !OPEN_SUCCESS_MODAL;

  const showSummaryCards = menuDetail.some(
    (m) => COMPACT_SUMMARY_MENU_IDS.has(m.menuId) && m.recordState === "A",
  );

  const rows = useMemo(() => {
    const visibleMenus: DashboardLayoutMenuDetail[] = (menuDetail ?? [])
      .filter(
        (m) =>
          m.recordState === "A" &&
          !COMPACT_SUMMARY_MENU_IDS.has(m.menuId) &&
          !COMPACT_WIDGET_HIDDEN_MENU_IDS.has(m.menuId) &&
          m.menuId !== 216 && // Temporarily hide Card Payment Due widget
          COMPACT_WIDGET_REGISTRY[m.menuId],
      )
      .sort((a, b) => a.orderSequence - b.orderSequence);

    const grouped = new Map<number, DashboardLayoutMenuDetail[]>();

    visibleMenus.forEach((menu) => {
      const rowKey = COMPACT_WIDGET_ROW[menu.menuId] ?? menu.menuId;

      if (!grouped.has(rowKey)) {
        grouped.set(rowKey, []);
      }

      grouped.get(rowKey)!.push(menu);
    });

    return Array.from(grouped.entries())
      .sort(([a], [b]) => a - b)
      .map(([, menus]) => menus);
  }, [menuDetail]);

  const tenantId = useSelector(
    (state: RootState) => state.userData?.data?.tenantId,
  );

  useEffect(() => {
    if (!tenantId) return;

    (async () => {
      try {
        const payload = { type: "PAY" };
        const result = await gxAppStatusApi(payload);

        if (gxIsArray(result?.data)) {
          setStatusList(result.data);
        }
      } catch (err) {
        console.log(err);
      }
    })();
  }, [tenantId]);

  return (
    <div className="gx-compact-widgets-main">
      {showSummaryCards && <CompactWidgetsCardDetails />}

      {rows.map((rowMenus) => {
        const rowKey = rowMenus.map((m) => m.menuId).join("-");

        return (
          <div
            className={`gx-compact-widgets-row ${
              rowMenus.length === 2 ? "gx-compact-widgets-row--two" : ""
            }`}
            key={rowKey}
          >
            {rowMenus.map((menu) => {
              const Widget = COMPACT_WIDGET_REGISTRY[menu.menuId];

              let extraProps = {};
              if (menu.menuId === 64) {
                extraProps = { callRecent, statusList, callCount: CALL_COUNT };
              } else if (menu.menuId === 206) {
                extraProps = { ...MOCK_REPAIR_QUEUE_DATA };
              }

              return (
                <div
                  key={menu.menuId}
                  className={`gx-compact-widgets-cell ${
                    menu.menuId === 64 ? "gx-compact-widgets-cell--full" : ""
                  }`}
                >
                  <Widget
                    title={menu.menuLabelName}
                    isMandatory={menu.isMandatory}
                    {...extraProps}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default CompactWidgets;
